import React, { useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Header } from '@/components/Header';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';

/** Viewports below Tailwind `lg` (1024px): phone + tablet start with collapsed nav. */
function getInitialSidebarCollapsed() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(max-width: 1023px)').matches;
}

const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

const generateBreadcrumbs = (pathname, segmentLabels = {}) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = [{ label: 'Home', path: '/dashboard' }];

  paths.forEach((segment, index) => {
    const fullPath = '/' + paths.slice(0, index + 1).join('/');
    let label =
      segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    if (segmentLabels[fullPath]) {
      label = segmentLabels[fullPath];
    } else if (OBJECT_ID_RE.test(segment)) {
      label = 'Appointment';
    }
    breadcrumbs.push({ label, path: fullPath });
  });

  return breadcrumbs;
};

export const AppLayout = ({ children }) => {
  const location = useLocation();
  const { segmentLabels } = useBreadcrumb();
  const breadcrumbs = generateBreadcrumbs(location.pathname, segmentLabels);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    getInitialSidebarCollapsed
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
      
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-all duration-300',
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        <Header />
        
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="p-4 sm:p-6 max-w-full">
            {breadcrumbs.length > 1 && (
              <Breadcrumb className="mb-4">
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {index === breadcrumbs.length - 1 ? (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link to={crumb.path}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}
            
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};